import { Monitor, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { applyTheme, readTheme, type Theme } from "../theme";
import { useT } from "../i18n";

const OPTIONS: Array<{ value: Theme; key: string; icon: typeof Sun }> = [
  { value: "light", key: "shell.themeLight", icon: Sun },
  { value: "dark", key: "shell.themeDark", icon: Moon },
  { value: "system", key: "shell.themeSystem", icon: Monitor },
];

/**
 * Three buttons, not a switch.
 *
 * A two-state switch cannot express "follow my machine", and that is the state most
 * readers actually want — it is also the default here. A switch would have to pick
 * light or dark on first load and would then be wrong for half of them every
 * evening.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const t = useT();

  function choose(next: Theme) {
    setTheme(next);
    applyTheme(next);
  }

  return (
    <div className="theme-toggle" role="group" aria-label={t("shell.theme")}>
      {OPTIONS.map((option) => {
        const Icon = option.icon;
        const active = theme === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={active ? "active" : ""}
            onClick={() => choose(option.value)}
            // The label carries the word; the icon alone is not a name.
            aria-label={t(option.key)}
            aria-pressed={active}
            title={t(option.key)}
          >
            <Icon size={15} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
